import { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Clock, FileUp, Send } from 'lucide-react';
import { formatEuro } from '../../utils/currency';
import {
  invoiceRequestService,
  pendingLines,
  type FulfilledInvoice,
  type InvoiceRequest,
  type InvoiceRequestLine,
} from '../../services/invoiceRequestService';
import { useModal } from '../../contexts/ModalContext';
import { log } from '../../lib/logger';
import type { Client } from '../../types/client';
import type { MohitInvoice } from '../../types/campaign';
import { InvoiceRequestModal } from './InvoiceRequestModal';
import { InvoiceScanModal } from './InvoiceScanModal';

export interface InvoiceRequestContext {
  /** Null until the campaign is saved for the first time. */
  campaignId: string | null;
  /** Set instead of campaignId on a project. */
  projectoId?: string | null;
  campaignName: string;
  purchaseOrder?: string;
}

type InvoiceAmount = Pick<MohitInvoice, 'status' | 'amount_received' | 'invoice_number'>;

const ISSUED_STATUSES = ['Issued', 'Paid', 'Overdue'];
const round2 = (n: number) => Math.round(n * 100) / 100;
const formatDay = (iso: string) => new Date(iso).toLocaleDateString('pt-PT', { day: 'numeric', month: 'short' });

/**
 * "Pedir fatura" and the requests still waiting for their invoice. When an
 * invoice arrives, its PDF is read and handed to `onInvoiceReceived`, which
 * puts it in the campaign's invoice list.
 */
export function InvoiceRequestsBlock({
  context,
  client,
  totalAmount,
  invoices,
  onInvoiceReceived,
}: {
  context: InvoiceRequestContext;
  client: Client | null;
  totalAmount: number;
  invoices: InvoiceAmount[];
  onInvoiceReceived: (requestedAmount: number, invoice: FulfilledInvoice) => void | Promise<void>;
}) {
  const { toast, showConfirm } = useModal();
  const [requests, setRequests] = useState<InvoiceRequest[]>([]);
  const [asking, setAsking] = useState(false);
  const [receiving, setReceiving] = useState<{ request: InvoiceRequest; line: InvoiceRequestLine; index: number } | null>(null);

  const { campaignId, projectoId } = context;
  const saved = !!(campaignId || projectoId);

  const load = useCallback(async () => {
    if (!campaignId && !projectoId) return;
    try {
      setRequests(projectoId
        ? await invoiceRequestService.listByProjecto(projectoId)
        : await invoiceRequestService.listByCampaign(campaignId!));
    } catch (err) {
      // The block still works as before without requests (e.g. SQL not applied yet)
      log.warn('Invoice requests not loaded:', err);
    }
  }, [campaignId, projectoId]);

  useEffect(() => { load(); }, [load]);

  const pending = requests.filter(r => r.status === 'pending');
  const invoicedAmount = round2(invoices
    .filter(inv => ISSUED_STATUSES.includes(inv.status))
    .reduce((s, inv) => s + (inv.amount_received || 0), 0));
  const requestedAmount = round2(pending.flatMap(pendingLines).reduce((s, l) => s + l.amount, 0));
  const plannedAmounts = invoices
    .filter(inv => inv.status === 'Not Issued' && !inv.invoice_number)
    .map(inv => inv.amount_received || 0);

  async function handleCancel(request: InvoiceRequest) {
    const confirmed = await showConfirm({
      title: 'Cancelar o pedido?',
      message: 'Quem emite as faturas deixa de o ver como pendente.',
      confirmText: 'Cancelar pedido',
      cancelText: 'Voltar',
      variant: 'danger',
    });
    if (!confirmed) return;
    try {
      const updated = await invoiceRequestService.cancel(request.id);
      setRequests(prev => prev.map(r => r.id === updated.id ? updated : r));
      toast.success('Pedido cancelado');
    } catch (err) {
      toast.error(err instanceof Error && err.message ? err.message : 'Não foi possível cancelar o pedido');
    }
  }

  async function handleReceived(invoice: FulfilledInvoice) {
    if (!receiving) return;
    try {
      const updated = await invoiceRequestService.fulfilLine(receiving.request.id, receiving.line.id, invoice);
      setRequests(prev => prev.map(r => r.id === updated.id ? updated : r));
      await onInvoiceReceived(receiving.line.amount, invoice);
      setReceiving(null);
      // A project's invoices save as you go; a campaign's wait for its Guardar
      toast.success(projectoId ? 'Fatura registada' : 'Fatura registada. Guarda a campanha para a gravar.');
    } catch (err) {
      toast.error(err instanceof Error && err.message ? err.message : 'Não foi possível registar a fatura');
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setAsking(true)}
        disabled={!saved}
        title={saved ? undefined : 'Guarda a campanha primeiro'}
        className="cm-btn cm-btn-primary !h-9 !px-4"
      >
        <Send className="w-4 h-4" />
        Pedir fatura
      </button>

      {pending.length > 0 && (
        <div className="basis-full order-last space-y-2" data-testid="pending-invoice-requests">
          {pending.map(request => (
            <div key={request.id} className="rounded-xl border border-amber-200 bg-amber-50/60 p-3 space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs text-amber-900">
                  <Clock className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />
                  Pedido por <strong>{request.requested_by_name || 'alguém'}</strong> · {formatDay(request.created_at)} · {request.lines.length} {request.lines.length === 1 ? 'fatura' : 'faturas'} · {formatEuro(request.total)}
                </p>
                <button type="button" onClick={() => handleCancel(request)} className="text-xs text-amber-800 hover:underline">
                  Cancelar pedido
                </button>
              </div>
              {request.lines.map((line, i) => (
                <div key={line.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-white px-3 py-2">
                  <span className="text-sm text-gray-800">
                    Fatura {i + 1}{request.lines.length > 1 ? `/${request.lines.length}` : ''} · <strong className="tabular-nums">{formatEuro(line.amount)}</strong>
                  </span>
                  {line.fulfilled_at ? (
                    <span className="cm-chip cm-chip-success"><CheckCircle2 className="w-3 h-3" /> Registada · Nº {line.invoice_number}</span>
                  ) : (
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="cm-chip cm-chip-warning whitespace-nowrap">Aguarda emissão</span>
                      <button type="button" onClick={() => setReceiving({ request, line, index: i })} className="cm-btn cm-btn-secondary cm-btn-sm whitespace-nowrap">
                        <FileUp className="w-3.5 h-3.5" />
                        Carregar fatura
                      </button>
                    </span>
                  )}
                </div>
              ))}
              {(request.purchase_order || request.notes) && (
                <p className="text-xs text-amber-900/80 whitespace-pre-wrap">
                  {request.purchase_order && <>OC {request.purchase_order}{request.notes ? ' · ' : ''}</>}
                  {request.notes}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {asking && saved && (
        <InvoiceRequestModal
          campaignId={campaignId}
          projectoId={projectoId}
          campaignName={context.campaignName}
          client={client}
          totalAmount={totalAmount}
          invoicedAmount={invoicedAmount}
          requestedAmount={requestedAmount}
          plannedAmounts={plannedAmounts}
          defaultPurchaseOrder={context.purchaseOrder || ''}
          onClose={() => setAsking(false)}
          onCreated={request => {
            setRequests(prev => [...prev, request]);
            setAsking(false);
          }}
        />
      )}

      {receiving && (
        <InvoiceScanModal
          label={`Fatura ${receiving.index + 1}${receiving.request.lines.length > 1 ? `/${receiving.request.lines.length}` : ''} · ${formatEuro(receiving.line.amount)} · ${receiving.request.client_name || context.campaignName}`}
          requestedAmount={receiving.line.amount}
          clientVat={receiving.request.client_vat || client?.vat_number || null}
          defaultPurchaseOrder={receiving.request.purchase_order || context.purchaseOrder || ''}
          onClose={() => setReceiving(null)}
          onConfirm={handleReceived}
        />
      )}
    </>
  );
}
